import { Link } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import type { PickingSummary } from '../../types';

interface PickingSummaryCardProps {
  summary?: PickingSummary;
  isLoading?: boolean;
}

export function PickingSummaryCard({ summary, isLoading }: PickingSummaryCardProps) {
  if (isLoading) {
    return (
      <Card>
        <CardHeader>
          <Skeleton className="h-5 w-32" />
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-3 gap-4">
            <Skeleton className="h-16" />
            <Skeleton className="h-16" />
            <Skeleton className="h-16" />
          </div>
        </CardContent>
      </Card>
    );
  }

  if (!summary) return null;

  // Get active list count from by_status
  const getStatusCount = (status: string) => {
    const item = summary.by_status?.find((s) => s.status === status);
    return item?.count || 0;
  };

  const activeLists = getStatusCount('pending') + getStatusCount('assigned') + getStatusCount('in_progress');

  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between">
        <CardTitle>Picking Overview</CardTitle>
        <Button variant="outline" size="sm" asChild>
          <Link to="/wms/picking">View All</Link>
        </Button>
      </CardHeader>
      <CardContent>
        <div className="grid grid-cols-3 gap-4">
          <div className="text-center p-4 bg-muted/50 rounded-lg">
            <div className="text-2xl font-bold text-blue-600">
              {activeLists}
            </div>
            <div className="text-sm text-muted-foreground">Active Lists</div>
          </div>
          <div className="text-center p-4 bg-muted/50 rounded-lg">
            <div className="text-2xl font-bold text-orange-600">
              {summary.today_stats?.items_picked || 0}
            </div>
            <div className="text-sm text-muted-foreground">Items Picked</div>
          </div>
          <div className="text-center p-4 bg-muted/50 rounded-lg">
            <div className="text-2xl font-bold text-green-600">
              {summary.today_stats?.lists_completed || 0}
            </div>
            <div className="text-sm text-muted-foreground">Completed Today</div>
          </div>
        </div>

        {summary.today_stats?.shortages > 0 && (
          <div className="mt-4 pt-4 border-t text-sm text-orange-600">
            <span className="font-medium">{summary.today_stats.shortages}</span> shortage(s) reported today
          </div>
        )}

        {summary.active_pickers && summary.active_pickers.length > 0 && (
          <div className="mt-4 pt-4 border-t">
            <div className="text-sm font-medium mb-2">Active Pickers</div>
            <div className="space-y-1">
              {summary.active_pickers.slice(0, 3).map((picker) => (
                <div key={picker.user_id} className="flex justify-between text-sm">
                  <span className="text-muted-foreground">{picker.user_name || 'Unknown'}</span>
                  <span className="font-medium">{picker.active_list_count} list(s)</span>
                </div>
              ))}
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
